import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Grid, 
  Tabs, 
  Tab, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  IconButton,
  useTheme,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { sampleProjects, projectTemplates, tutorialProjects } from '../data/sampleProjects';
import { supabase } from '../supabaseClient';

// Styled components
const ProjectCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 24px rgba(0, 0, 0, 0.2)' 
    : '0 8px 24px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(26, 26, 37, 0.95)' 
    : '#FFFFFF',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 12px 32px rgba(0, 0, 0, 0.3)' 
      : '0 12px 32px rgba(0, 0, 0, 0.1)',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 12px rgba(138, 43, 226, 0.3)' 
    : '0 4px 12px rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 6px 16px rgba(138, 43, 226, 0.4)' 
      : '0 6px 16px rgba(138, 43, 226, 0.3)',
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    minWidth: 120,
    borderRadius: 12,
    padding: '12px 24px',
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(138, 43, 226, 0.1)' 
        : 'rgba(138, 43, 226, 0.05)',
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(0, 0, 0, 0.05)',
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    }
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: 1.5,
  }
}));

const SampleProjectsGallery = ({ onSelectProject, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [session, setSession] = useState(null);
  
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };
    
    checkSession();
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleProjectSelect = (project) => {
    if (project.category === 'tutorial') {
      if (!session) {
        navigate('/login', {
          state: {
            returnUrl: '/workflows',
            message: 'Eğitimleri görüntülemek için lütfen giriş yapın veya kaydolun'
          }
        });
        if (onClose) onClose();
        return;
      }
      
      setSelectedProject(project);
      setTutorialOpen(true);
      setActiveStep(0);
    } else {
      if (!session) {
        navigate('/login', {
          state: {
            returnUrl: `/canvas-editor?workflow=${project.workflow || 'interior-redesign'}`,
            message: 'Bu şablonu kullanmak için lütfen giriş yapın veya kaydolun'
          }
        });
        if (onClose) onClose();
        return;
      }
      
      if (onSelectProject) {
        onSelectProject(project);
      }
    }
  };
  
  const handleTutorialClose = () => {
    setTutorialOpen(false);
  };
  
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  const handleTutorialComplete = () => {
    setTutorialOpen(false);
    
    // Navigate to canvas editor with the tutorial project
    if (selectedProject) {
      navigate(`/canvas-editor?workflow=${selectedProject.workflow}`, {
        state: {
          sampleProject: selectedProject
        }
      });
    }
  };
  
  // Get projects based on current tab
  const getProjects = () => {
    switch (tabValue) {
      case 0: // All
        return [...sampleProjects, ...projectTemplates];
      case 1: // Samples
        return sampleProjects;
      case 2: // Templates
        return projectTemplates;
      case 3: // Tutorials
        return tutorialProjects;
      default:
        return [];
    }
  };
  
  const currentProjects = getProjects();
  
  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
        Örnek Projeler, Şablonlar ve Eğitimler
      </Typography>
      
      <StyledTabs
        value={tabValue}
        onChange={handleTabChange}
        centered
        variant="fullWidth"
      >
        <Tab label="Tümü" />
        <Tab label="Örnekler" />
        <Tab label="Şablonlar" />
        <Tab label="Eğitimler" />
      </StyledTabs>
      
      <Grid container spacing={3}>
        {currentProjects.map((project) => (
          <Grid item xs={12} sm={6} md={4} key={project.id}>
            <ProjectCard>
              <CardMedia
                component="img"
                height="200"
                image={project.thumbnail}
                alt={project.title}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {project.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {project.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {project.style} stil
                  </Typography>
                  <StyledButton
                    variant="contained"
                    size="small"
                    onClick={() => handleProjectSelect(project)}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: '#FFFFFF',
                    }}
                  >
                    {project.category === 'tutorial' 
                      ? (session ? 'Eğitimi Başlat' : 'Giriş Yap') 
                      : (session ? 'Kullan' : 'Giriş Yap')}
                  </StyledButton>
                </Box>
              </CardContent>
            </ProjectCard>
          </Grid>
        ))}
      </Grid>
      
      {/* Tutorial Dialog */}
      <Dialog
        open={tutorialOpen}
        onClose={handleTutorialClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(26, 26, 37, 0.95)' 
              : '#FFFFFF',
            backgroundImage: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(138, 43, 226, 0.05) 0%, rgba(26, 26, 37, 0) 100%)'
              : 'none',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
              : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        {selectedProject && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              pb: 2
            }}>
              <Typography variant="h6">
                {selectedProject.title} - Adım Adım Eğitim
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleTutorialClose}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ py: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Stepper activeStep={activeStep} orientation="vertical">
                    {selectedProject.steps.map((step, index) => (
                      <Step key={index}>
                        <StepLabel>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {step.title}
                          </Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {step.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            {index > 0 && (
                              <Button
                                variant="outlined"
                                onClick={handleBack}
                                sx={{ textTransform: 'none' }}
                              >
                                Geri
                              </Button>
                            )}
                            {index < selectedProject.steps.length - 1 ? (
                              <Button
                                variant="contained"
                                onClick={handleNext}
                                sx={{ 
                                  textTransform: 'none',
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#FFFFFF',
                                }}
                              >
                                İleri
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                onClick={handleTutorialComplete}
                                endIcon={<ArrowForwardIcon />}
                                sx={{ 
                                  textTransform: 'none',
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#FFFFFF',
                                }}
                              >
                                {session ? 'Eğitimi Tamamla' : 'Giriş Yap'}
                              </Button>
                            )}
                          </Box>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Paper elevation={3} sx={{ 
                    p: 1, 
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: 400,
                    height: 'auto',
                    aspectRatio: '4/3',
                    overflow: 'hidden'
                  }}>
                    <img 
                      src={selectedProject.steps[activeStep].image} 
                      alt={`Step ${activeStep + 1}`}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: 8
                      }}
                    />
                  </Paper>
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ 
              justifyContent: 'space-between', 
              px: 3, 
              py: 2,
              borderTop: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            }}>
              <Button 
                onClick={handleTutorialClose}
                sx={{ 
                  color: 'text.secondary',
                  textTransform: 'none'
                }}
              >
                Kapat
              </Button>
              
              <Typography variant="body2" color="text.secondary">
                Adım {activeStep + 1} / {selectedProject.steps.length}
              </Typography>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SampleProjectsGallery;
